const VALIDATE_URL = "/api/slip-inventory/v1/slip-inventory/validate";
const CONSOLIDATE_URL = "/api/slip-inventory/v1/slip-inventory/consolidate";

// Colors mirror MULTISLIP_INTEGRITY_INFO in src/tools/slip-delivery/index.js, so an
// issue's dot here matches what the map already shows for that slip.
const ISSUE_KEY_COLORS = {
  missing_id: "#eab308",
  orphan: "#ef4444",
  disconnected: "#f97316",
  x_dock_not_consolidated: "#38bdf8",
  multislip_not_consolidated: "#38bdf8",
  marina_name_mismatch: "#a855f7",
  marina_not_in_catalog: "#f97316",
  catalog_unavailable: "#94a3b8",
  // Geometry / identity rules (slip-api checkGeometryValidity & co.)
  geometry_unparsable: "#ef4444",
  geometry_null_island: "#ef4444",
  geometry_point_only: "#f97316",
  geometry_outlier: "#eab308",
  duplicate_slip_id_exact: "#ef4444",
  duplicate_slip_id: "#eab308",
  region_partial: "#38bdf8",
};
const DEFAULT_ISSUE_COLOR = "#94a3b8";
const CLEAN_COLOR = "#22c55e";
const ISSUE_COLOR = "#ef4444";

// Rules with a one-click server-side fix for every flagged marina (see slip-api's
// /consolidate/{x-dock,multislip,marina-name}: the first two are ports of the editor's
// per-marina "Consolidate X Dock" / "Consolidate Multislip IDs" buttons, the third writes
// the project-settings name onto every row). Everything else is review-and-fix-by-hand
// for now, on purpose.
const MASS_SOLVERS = {
  x_dock_consolidation: { path: "x-dock", label: "Consolidate X Dock", effect: "renumbers X dock slips in each of those marinas exactly as the editor's per-marina button would" },
  multislip_consolidation: { path: "multislip", label: "Consolidate Multislip IDs", effect: "renumbers multislip groups in each of those marinas exactly as the editor's per-marina button would" },
  marina_name_consistency: { path: "marina-name", label: "Apply Project Names", effect: "sets every slip's marina_name in those marinas to the name in project settings (marinas with no project entry are skipped)" },
};

function esc(v) { return String(v ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;"); }

let app = null;
let container = null;
let loading = false;
let solving = null; // ruleId of the mass solver currently running
let error = null;
let notice = null;
let result = null; // { checkedAt, recordCount, rules, issueCount, issues }
let searchText = "";
// Every group starts collapsed; these remember what the user has opened across re-renders.
const openRules = new Set();
const openMarinas = new Set(); // "ruleId|marinaId"

async function runValidation() {
  loading = true;
  error = null;
  render();
  try {
    const r = await fetch(VALIDATE_URL);
    const data = await r.json();
    if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`);
    result = data;
  } catch (err) {
    error = err.message || String(err);
  } finally {
    loading = false;
    render();
  }
}

function issuesForRule(ruleId) {
  return result ? result.issues.filter((issue) => issue.ruleId === ruleId) : [];
}

function matchesSearch(issue) {
  const q = searchText.trim().toLowerCase();
  if (!q) return true;
  return [issue.marinaId, issue.marinaName, issue.slipId, issue.message]
    .some((v) => String(v || "").toLowerCase().includes(q));
}

function groupByMarina(issues) {
  const groups = new Map();
  for (const issue of issues) {
    const key = issue.marinaId || "—";
    if (!groups.has(key)) groups.set(key, { marinaId: issue.marinaId, marinaName: issue.marinaName, issues: [] });
    groups.get(key).issues.push(issue);
  }
  return [...groups.values()].sort((a, b) => String(a.marinaId || "").localeCompare(String(b.marinaId || "")));
}

async function runMassSolver(ruleId) {
  const solver = MASS_SOLVERS[ruleId];
  if (!solver || solving || !result) return;
  const marinaIds = [...new Set(issuesForRule(ruleId).map((i) => i.marinaId).filter(Boolean))];
  if (!marinaIds.length) return;
  const ok = window.confirm(
    `${solver.label} for ${marinaIds.length} marina${marinaIds.length === 1 ? "" : "s"} (${marinaIds.join(", ")})?\n\n`
    + `This ${solver.effect}, and can't be undone from here.`,
  );
  if (!ok) return;
  solving = ruleId;
  error = null;
  notice = null;
  render();
  try {
    const r = await fetch(`${CONSOLIDATE_URL}/${solver.path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ marinaIds }),
    });
    const data = await r.json();
    if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`);
    const touched = data.marinas.filter((m) => m.renamed > 0);
    const skipped = data.marinas.filter((m) => m.skipped);
    notice = `${solver.label}: updated ${data.renamed} record${data.renamed === 1 ? "" : "s"} across ${touched.length} marina${touched.length === 1 ? "" : "s"}`
      + (touched.length ? ` (${touched.map((m) => `${m.marinaId} ${m.renamed}`).join(", ")})` : "")
      + (skipped.length ? `; skipped ${skipped.map((m) => `${m.marinaId} (${m.skipped})`).join(", ")}` : "") + ".";
    app?.ui?.toast?.(notice);
  } catch (err) {
    error = `${solver.label} failed: ${err.message || String(err)}`;
  } finally {
    solving = null;
  }
  await runValidation();
}

function renderIssue(issue) {
  return `
    <div style="display:flex;align-items:flex-start;gap:8px;padding:5px 0;border-bottom:1px solid rgba(148,163,184,0.08);">
      <span style="flex:0 0 auto;margin-top:4px;width:8px;height:8px;border-radius:50%;background:${ISSUE_KEY_COLORS[issue.key] || DEFAULT_ISSUE_COLOR};"></span>
      <div style="flex:1;min-width:0;">
        ${issue.slipId || issue.measurementId ? `<div class="small" style="font-weight:600;">${esc(issue.slipId || issue.measurementId)}</div>` : ""}
        <div class="small" style="color:var(--muted);">${esc(issue.message)}</div>
      </div>
    </div>`;
}

function renderRule(rule) {
  const all = issuesForRule(rule.id);
  const shown = all.filter(matchesSearch);
  const clean = all.length === 0;
  const color = clean ? CLEAN_COLOR : ISSUE_COLOR;
  const marinas = groupByMarina(shown);
  const marinaCount = new Set(all.map((i) => i.marinaId || "—")).size;
  const solver = MASS_SOLVERS[rule.id];
  const isOpen = openRules.has(rule.id);
  const busy = solving === rule.id;
  const solverBtn = solver && !clean
    ? `<button type="button" class="btn" data-mass-solve="${esc(rule.id)}" ${solving || loading ? "disabled" : ""} style="font-size:10px;padding:2px 8px;margin-left:auto;">${busy ? "Running…" : `${esc(solver.label)} — all ${marinaCount} marina${marinaCount === 1 ? "" : "s"}`}</button>`
    : "";
  const countText = clean
    ? "clean"
    : `${all.length} issue${all.length === 1 ? "" : "s"} in ${marinaCount} marina${marinaCount === 1 ? "" : "s"}${shown.length !== all.length ? ` (${shown.length} match filter)` : ""}`;
  const body = clean
    ? `<div class="small" style="color:var(--muted);padding:6px 0 2px;">No marinas have this issue.</div>`
    : !marinas.length
      ? `<div class="small" style="color:var(--muted);padding:6px 0 2px;">No issues match the current filter.</div>`
      : marinas.map((group) => {
        const key = `${rule.id}|${group.marinaId || "—"}`;
        return `
          <details data-marina-group="${esc(key)}" ${openMarinas.has(key) ? "open" : ""} style="margin:4px 0 4px 6px;">
            <summary class="small" style="cursor:pointer;padding:4px 0;font-weight:600;">
              ${esc(group.marinaName || group.marinaId || "Unassigned")} <span style="color:var(--muted);font-weight:400;">(${esc(group.marinaId || "—")}) — ${group.issues.length} issue${group.issues.length === 1 ? "" : "s"}</span>
            </summary>
            <div style="padding:0 0 4px 14px;">${group.issues.map(renderIssue).join("")}</div>
          </details>`;
      }).join("");
  return `
    <details class="toolset-group" data-rule-group="${esc(rule.id)}" ${isOpen ? "open" : ""} style="border-left:3px solid ${color};margin-bottom:6px;">
      <summary style="display:flex;align-items:center;gap:8px;">
        <span style="flex:0 0 auto;width:9px;height:9px;border-radius:50%;background:${color};"></span>
        <span style="font-weight:600;">${esc(rule.label)}</span>
        <span class="small" style="color:${clean ? CLEAN_COLOR : "var(--muted)"};">${countText}</span>
        ${solverBtn}
      </summary>
      <div class="toolset-content">${body}</div>
    </details>`;
}

function render() {
  if (!container) return;
  const active = container.contains(document.activeElement) ? document.activeElement : null;
  const focusId = active?.id || null;
  const selStart = active?.selectionStart;
  const selEnd = active?.selectionEnd;

  const summary = result
    ? `<span class="small" style="color:var(--muted);">${result.recordCount.toLocaleString()} records checked at ${new Date(result.checkedAt).toLocaleString()}</span>`
    : `<span class="small" style="color:var(--muted);">Not run yet.</span>`;

  const cleanRules = result ? result.rules.filter((r) => issuesForRule(r.id).length === 0).length : 0;
  const body = !result
    ? `<div class="small" style="color:var(--muted);padding:10px;">Run validation to check the entire slip inventory database for rule violations.</div>`
    : result.rules.map(renderRule).join("");

  container.innerHTML = `
    <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
      <div style="display:flex;align-items:center;gap:8px;padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);">
        <span class="small" style="font-weight:600;flex:1;">DATA VALIDATION</span>
      </div>
      <div style="padding:10px;">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px;">
          <button id="sv-run" class="btn" ${loading || solving ? "disabled" : ""}>${loading ? "Running…" : result ? "Refresh" : "Run Validation"}</button>
          ${summary}
        </div>
        ${error ? `<div class="small" style="color:#ef4444;margin-bottom:8px;">${esc(error)}</div>` : ""}
        ${notice ? `<div class="small" style="color:${CLEAN_COLOR};margin-bottom:8px;">${esc(notice)}</div>` : ""}
        ${result ? `
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
            <span class="small" style="font-weight:600;color:${result.issueCount ? ISSUE_COLOR : CLEAN_COLOR};">${result.issueCount} issue${result.issueCount === 1 ? "" : "s"}</span>
            <span class="small" style="color:var(--muted);">${cleanRules}/${result.rules.length} rules clean</span>
            <input id="sv-search" type="text" placeholder="Filter by marina, slip, message…" value="${esc(searchText)}" style="flex:1;min-width:160px;" />
          </div>
        ` : ""}
      </div>
      <div style="border-top:1px solid rgba(148,163,184,0.1);padding:8px 10px 10px;">
        ${body}
      </div>
    </div>
  `;
  bindEvents();

  if (focusId) {
    const toRefocus = container.querySelector(`#${focusId}`);
    if (toRefocus) {
      toRefocus.focus();
      if (typeof selStart === "number" && typeof toRefocus.setSelectionRange === "function") {
        try { toRefocus.setSelectionRange(selStart, selEnd); } catch {}
      }
    }
  }
}

function bindEvents() {
  if (!container) return;
  container.querySelector("#sv-run")?.addEventListener("click", runValidation);
  container.querySelector("#sv-search")?.addEventListener("input", (e) => { searchText = e.target.value; render(); });
  container.querySelectorAll("details[data-rule-group]").forEach((el) => {
    el.addEventListener("toggle", () => {
      const id = el.dataset.ruleGroup;
      if (el.open) openRules.add(id); else openRules.delete(id);
    });
  });
  container.querySelectorAll("details[data-marina-group]").forEach((el) => {
    el.addEventListener("toggle", () => {
      const key = el.dataset.marinaGroup;
      if (el.open) openMarinas.add(key); else openMarinas.delete(key);
    });
  });
  container.querySelectorAll("[data-mass-solve]").forEach((btn) => {
    // The button sits inside the <summary>; stop the click from also toggling the group.
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      runMassSolver(btn.dataset.massSolve);
    });
  });
}

export const slipValidationTool = {
  id: "slip-validation",
  name: "Data Validation",
  init() {},
  destroy() { container = null; },
  renderPanel(appRef, panelEl) {
    app = appRef;
    container = panelEl;
    render();
    if (!result && !loading) runValidation();
  },
};
